import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { t } from '../i18n';
import { generateQuestion, formatTime } from '../gameEngine';
import { generateMCQuestion } from '../questionBanks';
import Keypad from '../components/game/Keypad';
import { motion, AnimatePresence } from 'framer-motion';
import {
  playCorrectSound, playWrongSound, playStreakSound,
  playSuperStreakSound, playCountdownSound, playGoSound,
  playWinSound, playTimerUrgentSound, playKeypadSound
} from '../soundEngine';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PracticePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const settings = location.state;
  const lastUrgentRef = useRef(0);

  const isMCQ = settings?.subjectType === 'mcq';
  const subjectId = settings?.subjectId || 'matematik';
  const gameLang = settings?.language || language;

  const [countdown, setCountdown] = useState(3);
  const [gameStarted, setGameStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(settings?.duration || 60);
  const [gameOver, setGameOver] = useState(false);

  const [question, setQuestion] = useState(null);
  const [input, setInput] = useState('');
  const [score, setScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [comboText, setComboText] = useState(null);
  const [mcqSelected, setMcqSelected] = useState(null);

  const genQ = useCallback(() => {
    if (isMCQ) return generateMCQuestion(subjectId, settings?.difficulty || 'easy', gameLang);
    return generateQuestion(settings?.difficulty || 'easy', settings?.operations || ['+']);
  }, [isMCQ, subjectId, settings, gameLang]);

  useEffect(() => {
    if (!settings) { navigate('/practice-setup'); return; }
    setQuestion(genQ());
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      playCountdownSound();
      const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !gameStarted) {
      playGoSound();
      setGameStarted(true);
    }
  }, [countdown, gameStarted]);

  useEffect(() => {
    if (gameStarted && !gameOver && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 11 && prev > 1) {
            const now = Date.now();
            if (now - lastUrgentRef.current > 900) { playTimerUrgentSound(); lastUrgentRef.current = now; }
          }
          if (prev <= 1) { setGameOver(true); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameStarted, gameOver, timeLeft]);

  useEffect(() => {
    if (gameOver && settings) {
      playWinSound();
      const savePractice = async () => {
        try {
          await fetch(`${BACKEND_URL}/api/practice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              player_name: settings.playerName || 'Player',
              score, correct_answers: correct, wrong_answers: wrong,
              best_streak: bestStreak, difficulty: settings.difficulty,
              operations: settings.operations || [], duration: settings.duration,
            })
          });
        } catch (e) { console.error(e); }
      };
      savePractice();
    }
  }, [gameOver]);

  const processAnswer = useCallback((isCorrect) => {
    if (isCorrect) {
      const newStreak = streak + 1;
      let bonus = 0;
      if (newStreak >= 5) { bonus = 2; playSuperStreakSound(); setComboText(`SUPER x${newStreak}! +3`); }
      else if (newStreak >= 3) { bonus = 1; playStreakSound(); setComboText(`STREAK x${newStreak}! +2`); }
      else { playCorrectSound(); }
      setScore(prev => prev + 1 + bonus);
      setCorrect(prev => prev + 1);
      setStreak(newStreak);
      setBestStreak(prev => Math.max(prev, newStreak));
      setFeedback('correct');
      setInput('');
      setTimeout(() => { setQuestion(genQ()); setFeedback(null); setComboText(null); setMcqSelected(null); }, 500);
    } else {
      playWrongSound();
      setWrong(prev => prev + 1);
      setStreak(0);
      setFeedback('wrong');
      setInput('');
      setTimeout(() => { setFeedback(null); setComboText(null); setMcqSelected(null); }, 500);
    }
  }, [streak, genQ]);

  // MCQ handler
  const handleMCQSelect = useCallback((idx) => {
    if (gameOver || !gameStarted || feedback !== null) return;
    playKeypadSound();
    setMcqSelected(idx);
    processAnswer(idx === question.answer);
  }, [gameOver, gameStarted, feedback, question, processAnswer]);

  // Keypad handlers
  const handleDigit = useCallback((_, digit) => {
    if (gameOver || !gameStarted) return;
    playKeypadSound();
    setInput(prev => prev.length < 4 ? prev + digit : prev);
  }, [gameOver, gameStarted]);

  const handleClear = useCallback(() => {
    if (gameOver || !gameStarted) return;
    setInput('');
  }, [gameOver, gameStarted]);

  const handleSubmit = useCallback(() => {
    if (gameOver || !gameStarted || input === '' || !question) return;
    processAnswer(input === question.answer.toString());
  }, [gameOver, gameStarted, input, question, processAnswer]);

  if (!settings) return null;

  const isUrgent = timeLeft <= 10 && timeLeft > 0;
  const feedbackClass = feedback === 'correct' ? 'feedback-correct' : feedback === 'wrong' ? 'feedback-wrong' : '';
  const animClass = feedback === 'correct' ? 'anim-correct' : feedback === 'wrong' ? 'anim-wrong' : '';
  const accuracy = correct + wrong > 0 ? Math.round((correct / (correct + wrong)) * 100) : 0;
  const mcqColors = ['#E85D3A', '#2196F3', '#4CAF50', '#FF9800'];

  return (
    <div className="practice-page">
      {/* Countdown */}
      <AnimatePresence>
        {countdown > 0 && (
          <motion.div className="countdown-overlay" initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div key={countdown} className="countdown-number"
              initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 2, opacity: 0 }} transition={{ duration: 0.6 }}>
              {countdown}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Game Over */}
      {gameOver && (
        <div className="practice-results-overlay">
          <motion.div className="practice-results-card"
            initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}>
            <h2 className="practice-results-title">{t('timesUp', language)}</h2>
            <div className="practice-results-score">{score}</div>
            <div className="practice-results-stats">
              <div className="practice-stat"><span>{t('correct', language)}</span><span className="practice-stat-correct">{correct}</span></div>
              <div className="practice-stat"><span>{t('wrong', language)}</span><span className="practice-stat-wrong">{wrong}</span></div>
              <div className="practice-stat"><span>{t('accuracy', language)}</span><span>{accuracy}%</span></div>
              <div className="practice-stat"><span>{t('bestStreak', language)}</span><span>{bestStreak}</span></div>
            </div>
            <div className="practice-results-actions">
              <button className="results-btn results-btn-play" onClick={() => navigate('/practice-setup')}>{t('playAgain', language)}</button>
              <button className="results-btn results-btn-home" onClick={() => navigate('/')}>{t('backToHome', language)}</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Practice Layout */}
      <div className="practice-layout">
        <div className="practice-stats-bar">
          <div className="practice-stat-chip">
            <span className="practice-stat-label">{t('score', language)}</span>
            <span className="practice-stat-value">{score}</span>
          </div>
          <div className={`timer-pill ${isUrgent ? 'timer-urgent' : ''}`}>{formatTime(timeLeft)}</div>
          <div className="practice-stat-chip">
            <span className="practice-stat-label">{streak >= 3 ? (streak >= 5 ? '\uD83D\uDD25' : '\u26A1') : ''} {t('accuracy', language)}</span>
            <span className="practice-stat-value">{accuracy}%</span>
          </div>
        </div>

        {/* Question */}
        <div className={`practice-question-card ${feedbackClass} ${animClass}`}>
          {question && (
            <div className={`practice-question-text ${isMCQ ? 'practice-question-mcq' : ''}`}>
              {isMCQ ? question.question : `${question.a} ${question.op} ${question.b} = ?`}
            </div>
          )}
        </div>

        {comboText && <div className="combo-text combo-super practice-combo">{comboText}</div>}

        {isMCQ ? (
          /* MCQ choices for practice */
          <div className="practice-mcq-choices">
            {(question?.options || []).map((option, idx) => {
              let btnClass = 'practice-mcq-btn';
              if (feedback !== null && idx === mcqSelected) {
                btnClass += feedback === 'correct' ? ' mcq-btn-correct' : ' mcq-btn-wrong';
              }
              return (
                <button
                  key={idx}
                  className={btnClass}
                  style={{ background: feedback === null ? mcqColors[idx] : undefined, opacity: (!gameStarted || gameOver) && feedback === null ? 0.5 : 1 }}
                  onClick={() => handleMCQSelect(idx)}
                  disabled={!gameStarted || gameOver || feedback !== null}
                  data-testid={`practice-mcq-option-${idx}`}
                >
                  <span className="mcq-letter">{String.fromCharCode(65 + idx)}</span>
                  <span className="mcq-text">{option}</span>
                </button>
              );
            })}
          </div>
        ) : (
          /* Math keypad */
          <>
            <div className={`practice-answer-pill ${feedbackClass} ${animClass}`}>{input || '0'}</div>
            <div className="practice-keypad-wrapper">
              <Keypad team="team1" onDigit={handleDigit} onClear={handleClear} onSubmit={handleSubmit} disabled={!gameStarted || gameOver} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
